/**
 * The `/artifacts` search parameters, and the mapping between them and the SharePoint tree's position.
 *
 * The section has four tabs, and the tab is in the URL rather than in a route of its own — the pattern
 * the Quality section already uses. The SharePoint tab keeps its position in the URL too, so a link opens
 * the tree exactly where the sender left it.
 *
 * The `sp_` prefix is load-bearing: `file` already means an artifact id here (the knowledge shelf links
 * back with it), so a bare `site`/`item` pair would give the two tabs one parameter with two meanings.
 */

import type { SharePointTreePosition } from '@/features/sharepoint/tree-position.js';

/** The section's tabs. An unknown value in the URL collapses to `undefined`, which reads as `files`. */
export type ArtifactsTab =
  'files' | 'archived' | 'sharepoint' | 'onedrive' | 'shared-with-me';

const TABS: readonly ArtifactsTab[] = [
  'files',
  'archived',
  'sharepoint',
  'onedrive',
  'shared-with-me',
];

export interface OneDrivePosition {
  scopeId: string | null;
  folderId: string | null;
  itemId: string | null;
}

/**
 * Re-exported from the shared browsing layer, which owns it: the composer's tree dialog carries the same
 * position without going through this section's URL rules.
 */
export type { SharePointTreePosition };

export interface ArtifactsSearch {
  tab?: ArtifactsTab;
  /** `?file=` deep-links one file's preview — what a knowledge-shelf row links back to. */
  file?: string;
  /**
   * Which SharePoint connection the tree belongs to. It sits BESIDE the position rather than inside it:
   * a position is where you are within one connection's tree, and this is whose tree that is.
   */
  sp_conn?: string;
  sp_site?: string;
  sp_drive?: string;
  sp_folder?: string;
  sp_item?: string;
  od_scope?: string;
  od_folder?: string;
  od_item?: string;
}

/** A trimmed id, or undefined when the parameter is missing, blank or not a string. */
function id(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Validate the URL into the section's search shape. Unknown values normalize away rather than reaching a
 * component: a blank id is no deep link, and a tab nobody has heard of is the files list.
 */
export function normalizeArtifactsSearch(
  raw: Record<string, unknown>,
): ArtifactsSearch {
  return {
    tab: TABS.includes(raw.tab as ArtifactsTab)
      ? (raw.tab as ArtifactsTab)
      : undefined,
    file: id(raw.file),
    sp_conn: id(raw.sp_conn),
    sp_site: id(raw.sp_site),
    sp_drive: id(raw.sp_drive),
    sp_folder: id(raw.sp_folder),
    sp_item: id(raw.sp_item),
    od_scope: id(raw.od_scope),
    od_folder: id(raw.od_folder),
    od_item: id(raw.od_item),
  };
}

export function oneDrivePositionFromSearch(
  search: ArtifactsSearch,
): OneDrivePosition {
  return {
    scopeId: search.od_scope ?? null,
    folderId: search.od_folder ?? null,
    itemId: search.od_item ?? null,
  };
}

export function oneDriveSearchFor(
  position: OneDrivePosition,
): Pick<ArtifactsSearch, 'od_scope' | 'od_folder' | 'od_item'> {
  return {
    od_scope: position.scopeId ?? undefined,
    od_folder: position.folderId ?? undefined,
    od_item: position.itemId ?? undefined,
  };
}

/**
 * The connection a link named, or `null` for none — which means whichever connection the service
 * resolves for the workspace, the way every link written before connections had ids reads.
 */
export function sharePointConnectionFromSearch(
  search: ArtifactsSearch,
): string | null {
  return search.sp_conn ?? null;
}

/**
 * The search parameter for a connection. Written on every change of connection, so leaving one drops
 * `sp_conn` from the URL instead of stranding the previous SharePoint's id there.
 */
export function sharePointConnectionSearchFor(
  connectionId: string | null,
): Pick<ArtifactsSearch, 'sp_conn'> {
  return { sp_conn: connectionId ?? undefined };
}

/** The tree position a link carries. */
export function sharePointPositionFromSearch(
  search: ArtifactsSearch,
): SharePointTreePosition {
  return {
    siteId: search.sp_site ?? null,
    driveId: search.sp_drive ?? null,
    folderId: search.sp_folder ?? null,
    itemId: search.sp_item ?? null,
  };
}

/**
 * The search parameters for a position. Every key is written on every move — an unset level goes back as
 * `undefined` so navigating up actually drops the deeper ids instead of leaving a stale folder in the URL.
 */
export function sharePointSearchFor(
  position: SharePointTreePosition,
): Pick<ArtifactsSearch, 'sp_site' | 'sp_drive' | 'sp_folder' | 'sp_item'> {
  return {
    sp_site: position.siteId ?? undefined,
    sp_drive: position.driveId ?? undefined,
    sp_folder: position.folderId ?? undefined,
    sp_item: position.itemId ?? undefined,
  };
}

/**
 * The parameters for a hit found by SEARCHING: the SharePoint it lives in and the place inside it, written
 * together. A search fans across every connection the caller reaches, so a hit can come from a SharePoint
 * other than the one on screen — and the two halves have to move in one step, because changing the
 * connection on its own resets the position and moving the position on its own would point the tree at
 * another tenant's ids.
 */
export function sharePointHitSearchFor(
  connectionId: string | null,
  position: SharePointTreePosition,
): Pick<
  ArtifactsSearch,
  'sp_conn' | 'sp_site' | 'sp_drive' | 'sp_folder' | 'sp_item'
> {
  return {
    ...sharePointConnectionSearchFor(connectionId),
    ...sharePointSearchFor(position),
  };
}
